import { Check, Gift, Star } from "lucide-react";
import { Reveal, Overline } from "../Reveal";
import { ADDONS } from "../../data/content";

// The one or two items per group worth calling out - picked for standalone
// dollar value, so the card reads as "look what's bundled in" at a glance.
const FLAGSHIP_ITEMS = new Set([
  "Dedicated Launch Manager",
  "Priority Support",
  "Ready-to-Sell Service Packages",
  "500+ Leads of Potential Clients in Your City",
  "6 Digital Marketing Courses",
]);

const TOTAL_ITEMS = ADDONS.reduce((n, g) => n + g.items.length, 0);

// One kit. Flagship items sit up top as highlight tiles, the rest follow as
// a two-column checklist so short and long kits end up a similar height.
// `wide` is the full-width dark variant used for the last (longest) kit.
function AddonCard({ group, index, wide }) {
  const flagship = group.items.filter((item) => FLAGSHIP_ITEMS.has(item));
  const rest = group.items.filter((item) => !FLAGSHIP_ITEMS.has(item));
  return (
    <div
      data-testid="pricing-addon-card"
      className={`relative h-full overflow-hidden rounded-3xl border p-6 sm:p-8 ${
        wide
          ? "border-[#0B1533] bg-[#0B1533] lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center lg:gap-12 lg:p-10"
          : "border-[#E6E9F1] bg-white mb-card-shadow"
      }`}
    >
      {wide && (
        <>
          <span aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#FF6B00]/25 blur-3xl" />
          <span aria-hidden className="pointer-events-none absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-[#2556BE]/25 blur-3xl" />
        </>
      )}

      <div className="relative">
        <div className="flex items-center gap-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#FF6B00] to-[#FF9A4D] text-white shadow-[0_8px_20px_rgba(255,107,0,0.35)]">
            <group.icon className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#FF6B00]">
              Kit {String(index + 1).padStart(2, "0")} · {group.items.length} included
            </div>
            <h3 className={`mt-1 text-lg font-bold leading-snug tracking-tight md:text-xl ${wide ? "text-white" : "text-[#0B1533]"}`}>
              {group.title}
            </h3>
          </div>
        </div>

        <ul className={`mt-6 grid gap-3 sm:grid-cols-2 ${wide ? "lg:grid-cols-1" : ""}`}>
          {flagship.map((item) => (
            <li
              key={item}
              className={`flex items-center gap-3 rounded-2xl border p-4 text-sm font-bold leading-snug ${
                flagship.length === 1 ? "sm:col-span-2" : ""
              } ${
                wide
                  ? "border-white/10 bg-white/[0.07] text-white"
                  : "border-[#FF6B00]/20 bg-gradient-to-br from-[#FFF3E9] to-white text-[#0B1533]"
              }`}
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#FF6B00]">
                <Star className="h-4 w-4 fill-white text-white" />
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <ul className={`relative mt-6 grid gap-x-6 gap-y-3.5 sm:grid-cols-2 ${wide ? "lg:mt-0" : ""}`}>
        {rest.map((item) => (
          <li
            key={item}
            className={`flex items-start gap-2.5 text-sm leading-snug ${wide ? "text-[#CBD5E1]" : "text-[#475569]"}`}
          >
            <span className={`mt-px grid h-5 w-5 shrink-0 place-items-center rounded-full ${wide ? "bg-[#FF6B00]/20" : "bg-[#FF6B00]/10"}`}>
              <Check className="h-3 w-3 text-[#FF6B00]" strokeWidth={3} />
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PricingAddons() {
  return (
    <section
      data-testid="pricing-addons-section"
      className="relative py-20 md:py-24 border-t border-[#E6E9F1] bg-[#F4F6FB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
        <Reveal className="max-w-2xl mx-auto text-center">
          <Overline>Nothing Held Back</Overline>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
            What's Included Before You Even Launch
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#475569] leading-relaxed">
            Setup, sales assets, training and lead lists - the parts most platforms make you buy separately ship
            free with every plan.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#FF6B00]/25 bg-white px-4 py-2 text-sm font-semibold text-[#0B1533] shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
            <Gift className="h-4 w-4 text-[#FF6B00]" />
            {TOTAL_ITEMS} extras bundled in
            <span className="text-[#FF6B00]">· free with every plan</span>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {ADDONS.map((group, i) => {
            const wide = i === ADDONS.length - 1;
            return (
              <Reveal key={group.title} delay={i * 0.08} className={wide ? "lg:col-span-2" : ""}>
                <AddonCard group={group} index={i} wide={wide} />
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
